import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Loader from '../../components/Loader';
import useActionNeededOrders from '../../hooks/fetch-hooks/order-guardrails/use-action-needed-orders';
import { RouteConstants } from '../../config/constants/route';

const PAGE_SIZE = 20;

const GUARDRAIL_CODE_LABELS = {
    PRESCRIPTION_MISSING: 'Missing prescription',
    PRESCRIPTION_NO_LINKS: 'No prescription docs',
    ADDRESS_MISSING: 'No address',
    ADDRESS_NOT_FOUND: 'Address not found',
    NO_MEDICINE_PAYMENT_ITEM: 'No medicine invoice',
    MEDICINE_NOT_LINKED_TO_INVENTORY: 'Medicine not linked',
    NO_INVOICE_PAYMENT_ITEM: 'No invoice item',
    PAYMENT_ITEMS_NOT_LINKED: 'Payment not linked',
    PAYMENT_ITEM_NO_PAYMENT_LINK: 'Invoice unpaid',
    PAYMENT_ITEM_UNDERPAID: 'Invoice underpaid',
    METADATA_INVALID_SOURCE: 'Invalid source',
    METADATA_INVALID_CHANNEL: 'Invalid channel',
    METADATA_MISSING_CHRONICITY: 'Missing chronicity',
    METADATA_INVALID_CHRONICITY: 'Invalid chronicity',
    PRESCRIPTION_ITEMS_MISSING: 'No prescription items',
    ITEM_DETAIL_NOT_VERIFIED: 'Items unverified',
    BILL_MISSING_INVENTORY_ID: 'No eVital ID',
    BILL_MISSING_BILL_NUMBER: 'No bill number',
    NO_ACTIVE_DELIVERY: 'No active delivery',
    GUARDRAIL_ERROR: 'Check error',
    ORDER_NOT_FOUND: 'Order not found',
};

const ALL_FAILURE_CODES = [
    'PRESCRIPTION_MISSING',
    'PRESCRIPTION_NO_LINKS',
    'ADDRESS_MISSING',
    'ADDRESS_NOT_FOUND',
    'NO_MEDICINE_PAYMENT_ITEM',
    'MEDICINE_NOT_LINKED_TO_INVENTORY',
    'PAYMENT_ITEMS_NOT_LINKED',
    'PAYMENT_ITEM_NO_PAYMENT_LINK',
    'PAYMENT_ITEM_UNDERPAID',
    'METADATA_INVALID_SOURCE',
    'METADATA_INVALID_CHANNEL',
    'METADATA_MISSING_CHRONICITY',
    'METADATA_INVALID_CHRONICITY',
    'PRESCRIPTION_ITEMS_MISSING',
    'ITEM_DETAIL_NOT_VERIFIED',
    'BILL_MISSING_INVENTORY_ID',
    'BILL_MISSING_BILL_NUMBER',
    'NO_ACTIVE_DELIVERY',
    'GUARDRAIL_ERROR',
];

const STATUS_OPTIONS = [
    'PENDING', 'OCS_PENDING', 'PROCESSING', 'QUALIFIED_LEAD', 'INVENTORY_CHECK',
    'CONFIRM_DIGITAL_RX', 'CONFIRM_PAYMENT', 'PACKAGING', 'SHIPPED', 'DELIVERED',
    'CANCELLED', 'RETURNED',
];

const STATUS_COLORS = {
    PENDING: 'bg-yellow-100 text-yellow-700',
    OCS_PENDING: 'bg-orange-100 text-orange-700',
    PROCESSING: 'bg-blue-100 text-blue-700',
    DELIVERED: 'bg-green-100 text-green-700',
    CANCELLED: 'bg-red-100 text-red-700',
    RETURNED: 'bg-gray-200 text-gray-700',
};

function ActionNeeded() {
    const [page, setPage] = useState(1);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [selectedCodes, setSelectedCodes] = useState([]);
    const [selectedStatuses, setSelectedStatuses] = useState([]);

    const failureCode = selectedCodes.length > 0 ? selectedCodes.join(',') : undefined;
    const status = selectedStatuses.length > 0 ? selectedStatuses.join(',') : undefined;

    const { data, isLoading } = useActionNeededOrders({
        page,
        size: PAGE_SIZE,
        failureCode,
        status,
        from: from || undefined,
        to: to || undefined,
    });

    const payload = data?.data?.data;
    const count = payload?.count ?? 0;
    const rows = payload?.rows ?? [];
    const maxPage = Math.max(1, Math.ceil(count / PAGE_SIZE));

    const toggleCode = (code) => {
        setSelectedCodes((prev) =>
            prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
        );
        setPage(1);
    };

    const toggleStatus = (s) => {
        setSelectedStatuses((prev) =>
            prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
        );
        setPage(1);
    };

    const clearFilters = () => {
        setFrom('');
        setTo('');
        setSelectedCodes([]);
        setSelectedStatuses([]);
        setPage(1);
    };

    const hasFilters = from || to || selectedCodes.length > 0 || selectedStatuses.length > 0;

    const editOrderPath = (id) => RouteConstants.EDIT_ORDER.replace(':orderId', id);

    return (
        <div className="p-7 h-full overflow-y-scroll bg-gray-50">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-xl font-semibold text-gray-800">Action Needed</h1>
                    {!isLoading && (
                        <p className="text-sm text-gray-500 mt-0.5">
                            {count} order{count !== 1 ? 's' : ''} with open guardrail issues
                        </p>
                    )}
                </div>
                {hasFilters && (
                    <button
                        onClick={clearFilters}
                        className="bg-red-500 text-white-50 px-4 py-1.5 rounded-md text-sm font-medium"
                    >
                        Clear Filters
                    </button>
                )}
            </div>

            {/* Date range */}
            <div className="flex gap-4 mb-4 flex-wrap">
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                    <input
                        type="date"
                        value={from}
                        onChange={(e) => { setFrom(e.target.value); if (to && e.target.value > to) setTo(''); setPage(1); }}
                        className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                    <input
                        type="date"
                        value={to}
                        min={from || undefined}
                        disabled={!from}
                        onChange={(e) => { setTo(e.target.value); setPage(1); }}
                        className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                </div>
            </div>

            {/* Status chips */}
            <div className="mb-3">
                <p className="text-xs font-medium text-gray-600 mb-1.5">Status</p>
                <div className="flex gap-2 flex-wrap">
                    {STATUS_OPTIONS.map((s) => (
                        <button
                            key={s}
                            onClick={() => toggleStatus(s)}
                            className={`text-xs px-2 py-1 rounded-lg flex items-center gap-1 ${selectedStatuses.includes(s) ? 'bg-elm-500 text-white-50' : 'bg-gray-200 text-gray-700'}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>

            {/* Failure code chips */}
            <div className="mb-5">
                <p className="text-xs font-medium text-gray-600 mb-1.5">Failure Code</p>
                <div className="flex gap-2 flex-wrap">
                    {ALL_FAILURE_CODES.map((code) => (
                        <button
                            key={code}
                            onClick={() => toggleCode(code)}
                            className={`text-xs px-2 py-1 rounded-lg ${selectedCodes.includes(code) ? 'bg-red-500 text-white-50' : 'bg-gray-200 text-gray-700'}`}
                        >
                            {GUARDRAIL_CODE_LABELS[code] || code}
                        </button>
                    ))}
                </div>
            </div>

            {/* Table */}
            {isLoading ? (
                <Loader size="large" text="Loading orders..." />
            ) : count === 0 ? (
                <div className="text-center py-16 text-gray-500">
                    <p className="text-2xl mb-2">🎉</p>
                    <p className="font-medium">No orders need attention</p>
                    {hasFilters && <p className="text-sm mt-1">Try clearing your filters.</p>}
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 text-xs text-gray-600 uppercase tracking-wide">
                                <tr>
                                    <th className="px-4 py-3">Order ID</th>
                                    <th className="px-4 py-3">Patient</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3">Created</th>
                                    <th className="px-4 py-3">Open</th>
                                    <th className="px-4 py-3">Issues</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {rows.map((row) => (
                                    <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3 font-medium">
                                            <Link
                                                to={editOrderPath(row.id)}
                                                className="text-blue-600 hover:underline"
                                            >
                                                #{row.id}
                                            </Link>
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">
                                            {row.patientName || <span className="text-gray-400 italic">—</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[row.status] || 'bg-gray-100 text-gray-700'}`}>
                                                {row.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                            {row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-IN') : '—'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                                {row.openCount}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex gap-1 flex-wrap">
                                                {(row.openCodes || []).map((code) => (
                                                    <span
                                                        key={code}
                                                        className="px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-600 border border-red-200"
                                                    >
                                                        {GUARDRAIL_CODE_LABELS[code] || code}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="btn_div mt-4">
                        <button
                            onClick={() => setPage((p) => p - 1)}
                            disabled={page <= 1}
                            className="previous"
                        >
                            Previous
                        </button>
                        <span className="pageindex">
                            {`Page ${page} of ${maxPage}`}
                        </span>
                        <button
                            onClick={() => setPage((p) => p + 1)}
                            disabled={page >= maxPage}
                            className="next"
                        >
                            Next
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

export { ActionNeeded };
export default ActionNeeded;
